import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Calendar, Loader, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

export default function AppointmentResponse() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [appointment, setAppointment] = useState(null);

  const token = searchParams?.get('token');
  const action = searchParams?.get('action');

  useEffect(() => {
    if (token) {
      loadAppointment();
    } else {
      setError('Invalid or missing response token');
      setLoading(false);
    }
  }, [token]);

  const loadAppointment = async () => {
    try {
      setLoading(true);
      setError('');

      const { data, error: fetchError } = await supabase
        ?.from('appointments')
        ?.select('*, bail_agents(*)')
        ?.eq('approval_token', token)
        ?.single();

      if (fetchError) {
        throw new Error('Appointment not found or token expired');
      }

      if (data?.status !== 'pending_client_approval') {
        throw new Error('This appointment has already been processed');
      }

      setAppointment(data);
    } catch (err) {
      console.error('Error loading appointment:', err);
      setError(err?.message || 'Failed to load appointment details');
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async (responseAction) => {
    try {
      setProcessing(true);
      setError('');

      const { data, error: responseError } = await supabase
        ?.rpc('client_responds_to_reassignment', {
          p_approval_token: token,
          p_action: responseAction
        });

      if (responseError) {
        throw new Error(`Response failed: ${responseError?.message}`);
      }

      // Show success message briefly then redirect
      setTimeout(() => {
        if (responseAction === 'reschedule') {
          navigate('/appointment-scheduling-system');
        } else {
          navigate('/');
        }
      }, 3000);
    } catch (err) {
      console.error('Error processing response:', err);
      setError(err?.message || 'Failed to process your response');
      setProcessing(false);
    }
  };

  useEffect(() => {
    if (action && appointment && !processing) {
      if (['approve', 'deny', 'reschedule']?.includes(action)) {
        handleResponse(action);
      }
    }
  }, [action, appointment]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading appointment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Error - Sailors Bail Bonds</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
            <div className="text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Process Request</h1>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={() => navigate('/')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Return to Homepage
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (processing) {
    return (
      <>
        <Helmet>
          <title>Processing Response - Sailors Bail Bonds</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
            <div className="text-center">
              {action === 'approve' && (
                <>
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">Appointment Approved!</h1>
                  <p className="text-gray-600 mb-4">
                    Thank you for confirming your appointment with {appointment?.bail_agents?.full_name}.
                    You will receive a confirmation email shortly.
                  </p>
                </>
              )}
              {action === 'deny' && (
                <>
                  <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">Appointment Cancelled</h1>
                  <p className="text-gray-600 mb-4">
                    Your appointment has been cancelled. If you would like to schedule a new appointment,
                    please visit our scheduling page or call us directly.
                  </p>
                </>
              )}
              {action === 'reschedule' && (
                <>
                  <Calendar className="w-16 h-16 text-blue-500 mx-auto mb-4" />
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">Redirecting to Scheduling...</h1>
                  <p className="text-gray-600 mb-4">
                    Please wait while we redirect you to our scheduling system to choose a new time.
                  </p>
                </>
              )}
              <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto" />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Respond to Appointment - Sailors Bail Bonds</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-center">
            <Calendar className="w-12 h-12 text-white mx-auto mb-2" />
            <h1 className="text-2xl font-bold text-white">Appointment Reassignment</h1>
            <p className="text-blue-100 text-sm">Please review and respond to this change</p>
          </div>

          {/* Content */}
          <div className="p-8">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Appointment Details</h2>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">New Team Member:</span>
                  <span className="font-semibold text-gray-900">{appointment?.bail_agents?.full_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span className="font-semibold text-gray-900">
                    {new Date(appointment?.appointment_date)?.toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Time:</span>
                  <span className="font-semibold text-gray-900">
                    {new Date(appointment?.appointment_date)?.toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Purpose:</span>
                  <span className="font-semibold text-gray-900">
                    {appointment?.purpose?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                  </span>
                </div>
              </div>
            </div>

            {appointment?.reassignment_reason && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Reason for Change:</h3>
                <p className="text-gray-700 text-sm">{appointment?.reassignment_reason}</p>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={() => handleResponse('approve')}
                className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Approve New Team Member
              </button>
              <button
                onClick={() => handleResponse('deny')}
                className="w-full bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <XCircle className="w-5 h-5" />
                Deny and Cancel Appointment
              </button>
              <button
                onClick={() => handleResponse('reschedule')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <Calendar className="w-5 h-5" />
                Reschedule Appointment
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 p-4 text-center border-t">
            <p className="text-sm text-gray-600">
              Need help? Call us 24/7 at{' '}
              <a href="tel:+19162563468" className="text-blue-600 font-semibold hover:text-blue-700">
                (916) 256-3468
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}